import React, {useState, useEffect, useCallback} from 'react'
import {useForm} from 'react-hook-form'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Flex,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    Button,
    Divider,
    IconButton
} from '@chakra-ui/react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCustomObject} from '../../hooks/use-custom-object'

import {getFlagIcon} from '../forms/useAddressFields'
import MultiCountriesFields from '../forms/multiCountries-fields'
import {findFullName} from '../../utils/custom-utils'
import {
    updateSitewithLocale,
    redirectSFRA,
    handleCountryRegionLanguage
} from '../../utils/multi-countries-utils'

const MultiCountriesPopUp = ({isOpen, openModal, closeModal, preSelectedData}) => {
    const form = useForm()
    const {reset, getValues} = form
    const {locale} = useMultiSite()
    const configs = getConfig()
    const [regions, setRegions] = useState([])
    const [countries, setCountries] = useState([])
    const [languages, setLanguages] = useState([])
    const [countryFlag, setCountryFlag] = useState()
    const [countryRegionsAndLanguage, setCountryRegionsAndLanguage] = useState()
    const instanceList = configs?.app?.library?.instanceList
        ?.split(',')
        .map((instance) => instance.trim())
    const validRegions = configs?.app?.library?.regionsList
        ?.split(',')
        .map((region) => region.trim())

    const {data: multiCountryList, isSuccess} = useCustomObject({objectTypeId: 'multiCountry'})
    const formatRegions = useCallback(
        (list) =>
            list?.map((item) => ({
                value: item.region,
                label: item.displayName,
                countries: item.countryList.split(', ').map((country) => ({value: country}))
            })),
        []
    )

    const formatCountries = useCallback(
        (list) =>
            list?.map((item) => ({
                value: item.countryCode,
                label: item.countryName,
                languages: item.languages.split(',').map((languages) => ({value: languages}))
            })),
        []
    )

    const formatLanguages = useCallback(
        (list) =>
            list?.map((item) => ({
                value: item.languageCode,
                label: item.languageName
            })),
        []
    )

    const resetFormWithData = (data) => {
        reset({
            region: data?.region?.value || '',
            country: data?.country?.value || '',
            language: data?.language?.value || ''
        })
    }

    useEffect(() => {
        if (isSuccess && multiCountryList?.content) {
            const {regionalList, countryList, lanaguageList} = multiCountryList.content
            setRegions(formatRegions(regionalList))
            setCountries(formatCountries(countryList))
            setLanguages(formatLanguages(lanaguageList))
        }
    }, [isSuccess, multiCountryList, formatRegions, formatCountries, formatLanguages])

    useEffect(() => {
        if (preSelectedData) {
            resetFormWithData(preSelectedData)
            setCountryRegionsAndLanguage(preSelectedData)
        } else if (locale?.id && regions?.length && countries?.length && languages?.length) {
            handleCountryRegionLanguage({
                locale,
                regions,
                countries,
                languages,
                findFullName,
                setCountryRegionsAndLanguage
            })
        }
    }, [preSelectedData, locale?.id, regions, countries, languages, reset])

    useEffect(() => {
        if (isOpen && countryRegionsAndLanguage) {
            resetFormWithData(countryRegionsAndLanguage)
        }
    }, [isOpen, countryRegionsAndLanguage, reset])

    useEffect(() => {
        const urlParams = new URLSearchParams(window?.location?.search)
        const region = urlParams.get('region')
        const country = urlParams.get('country')
        const language = urlParams.get('language')

        if (region && country && language) {
            const sessionObj = {
                region: {value: region, label: findFullName(region, regions)},
                country: {value: country, label: findFullName(country, countries)},
                language: {value: language, label: findFullName(language, languages)},
                flagIcon: country
            }
            localStorage.setItem('selectedCountryRegionLanguage', JSON.stringify(sessionObj))
            setCountryRegionsAndLanguage(sessionObj)
            window.history.replaceState({}, '', window.location.pathname)
        }
    }, [regions, countries, languages])

    const handleSave = () => {
        const values = getValues()
        const isInstance = instanceList.includes(configs.app.library.instanceName)
        const isRegion = validRegions.includes(values?.region)

        if (isInstance || isRegion) {
            updateSitewithLocale(regions, countries, languages, values, setCountryFlag, closeModal)
        } else {
            redirectSFRA(getConfig, values)
        }
    }

    return (
        <>
            <IconButton
                aria-label="Change country or region"
                onClick={openModal}
                height="48px"
                bgColor="unset"
                _hover={{bgColor: 'unset'}}
                icon={
                    <Box className="flagicon-link">
                        {getFlagIcon(
                            countryRegionsAndLanguage?.flagIcon ||
                                countryFlag ||
                                locale?.id?.split('-')[1] ||
                                'US'
                        )}
                    </Box>
                }
            />
            <Modal isOpen={isOpen} onClose={closeModal} motionPreset="slideInTop">
                <ModalOverlay />
                <ModalContent
                    maxWidth="375px"
                    borderRadius="4px"
                    background="#23252b"
                    color="textColorInvert"
                >
                    <ModalHeader padding="0">
                        <ModalCloseButton />
                    </ModalHeader>
                    <ModalBody textAlign="center" padding="20px">
                        <Box
                            as="h3"
                            fontSize="fontSize28"
                            fontWeight="extrabold"
                            lineHeight={8}
                            mb={2}
                        >
                            <FormattedMessage
                                defaultMessage="Choose Your Location"
                                id="location_selection_text"
                            />
                        </Box>
                        <Box fontSize="md" fontWeight="normal" lineHeight={5}>
                            <FormattedMessage
                                defaultMessage="Choose your delivery destination in order to see the correct product selection."
                                id="destination_selection_text"
                            />
                        </Box>
                        <Divider border="1px solid #d9d9d9" my="24px" opacity={1} />
                        {regions?.length > 0 && (
                            <>
                                <MultiCountriesFields
                                    form={form}
                                    regions={regions}
                                    countries={countries}
                                    languages={languages}
                                    sessionData={countryRegionsAndLanguage}
                                />
                                <Flex direction="column" gap="2" mt="6">
                                    <Button variant="primary" onClick={handleSave}>
                                        <FormattedMessage
                                            defaultMessage="Save"
                                            id="multi_countries_fields.button.save"
                                        />
                                    </Button>
                                    <Button
                                        onClick={closeModal}
                                        variant="outline"
                                        color="white"
                                        borderColor="white"
                                    >
                                        <FormattedMessage
                                            defaultMessage="Cancel"
                                            id="multi_countries_fields.button.cancel"
                                        />
                                    </Button>
                                </Flex>
                            </>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    )
}

MultiCountriesPopUp.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    openModal: PropTypes.func.isRequired,
    closeModal: PropTypes.func.isRequired,
    preSelectedData: PropTypes.object
}

export default MultiCountriesPopUp
